// Marketing › Ads and calls, Blueprint only (pages/ux-full screens/ads.js: #/home/ads, #/home/ads/campaigns,
// #/home/ads/leads). Each page answers one question with ui-web's views (AdsViews.tsx), each drawn from
// its own action's answer: how the ads did (reports.campaigns by channel, adSpend.list), which campaigns
// work (reports.campaigns by campaign beside campaigns.list, and reports.callReasons), and where today's
// calls came from (calls.recent, and adConversions.list for what went to Google, each a page at a time).
// How the ads did also holds the ads over time (reports.campaigns by day or week), a notice when an ad
// account's numbers stop coming in (adAccounts.status), and the suggested changes to make in the ad
// platforms by hand (AdChanges.tsx). Who sees them is ads-model.ts's (marketing.view).
import { useCallback, useEffect, useState, type ReactNode } from 'react';
import {
  AdsFunnel,
  AdsSummary,
  AdsSummaryProps,
  AdsTrend,
  AdsTrendProps,
  CallReasons,
  CallReasonsProps,
  CallsBySource,
  CallsBySourceProps,
  CampaignsReport,
  CampaignTable,
  CampaignTableProps,
  Conversions,
  ConversionsProps,
  Section,
  SpendProps,
  SpendThisMonth,
  type Host,
  type OpenTarget,
} from '@flowology/ui-web';
import '@flowology/ui-web/base.css';
import { client } from '../api.ts';
import { Header, type NavProps } from '../Nav.tsx';
import { monthName, monthOf, todayIn } from '../books/model.ts';
import { errorText, useQuery } from '../books/ui.tsx';
import { stepMonth } from '../huddle/huddle-model.ts';
import { Loading } from '../shared/Loading.tsx';
import {
  allSpend,
  maySeeConversions,
  monthRange,
  periodRange,
  PERIODS,
  leadsInput,
  sourceCountsInput,
  sources,
  trendRange,
  TREND_PRESETS,
  type Period,
  type SourceCount,
  type SpendPage,
  type TrendPreset,
} from './ads-model.ts';
import { AdChangesSection } from './AdChanges.tsx';
import { officeHost } from './host.ts';
import './home.css';

const tzOf = (nav: NavProps) => nav.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone;

/**
 * An answer drawn by its view once it parses as the view's props: still loading draws the loading
 * ladder, and an answer that drifted from its view says so rather than half-drawing.
 */
export function Drawn<T>({
  q,
  schema,
  what,
  build,
  draw,
}: {
  q: { data: unknown; error: string | null; reload: () => Promise<void> };
  /** The view's props schema (ui-web schemas.ts). */
  schema: { safeParse(v: unknown): { success: true; data: T } | { success: false } };
  what: string;
  /** The view's props from the answer: the answer and the page's own words. */
  build?: (data: never) => unknown;
  draw: (props: T) => ReactNode;
}) {
  if (q.data == null) return <Loading error={q.error} what={what} retry={() => void q.reload()} />;
  const parsed = schema.safeParse(build ? build(q.data as never) : q.data);
  if (!parsed.success)
    return (
      <p className="flw-empty" role="status">
        Flowology can’t show {what} here.
      </p>
    );
  return <>{draw(parsed.data)}</>;
}

/** A list a page at a time: the input's first page, then each next one under it, with the true total. */
function usePages<T>(action: string, input: Record<string, unknown> | null, field: string) {
  const key = JSON.stringify(input);
  const [page, setPage] = useState<{ key: string; rows: T[]; total: number; next: string | null } | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const load = useCallback(
    async (cursor?: string) => {
      if (input === null) return;
      try {
        const r = await client.call<Record<string, unknown>>(action, cursor ? { ...input, cursor } : input);
        const rows = (r[field] ?? []) as T[];
        setPage((p) => ({
          key,
          rows: cursor && p?.key === key ? [...p.rows, ...rows] : rows,
          total: typeof r.total === 'number' ? r.total : rows.length,
          next: typeof r.next === 'string' ? r.next : null,
        }));
        setError(null);
      } catch (e) {
        setError(errorText(e, 'Could not load this.'));
      }
    },
    // the input is compared by value (key), as useQuery's
    [action, key, field],
  );
  useEffect(() => {
    void load();
  }, [load]);
  const data = page?.key === key ? page : null;
  return {
    data,
    error,
    reload: () => load(),
    more: data?.next ? () => void load(data.next!) : undefined,
  };
}

/** Every ad spend entry in a range (list caps M11), as useQuery answers: the month's sum is of them all. */
function useAllSpend(range: { from: string; to: string }) {
  const key = JSON.stringify(range);
  const [got, setGot] = useState<{ key: string; entries: unknown[] } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const reload = useCallback(async () => {
    try {
      const entries = await allSpend(
        (input) => client.call<SpendPage<unknown>>('adSpend.list', input),
        range,
      );
      setGot({ key, entries });
      setError(null);
    } catch (e) {
      setError(errorText(e, 'Could not load this.'));
    }
    // the range is compared by value (key), as useQuery's
  }, [key]);
  useEffect(() => {
    void reload();
  }, [reload]);
  return { data: got?.key === key ? { entries: got.entries } : null, error, reload };
}

/** What stops an ad account's numbers coming in (adAccounts.status), said once at the top. */
function AccountNotices() {
  const q = useQuery<{ notices: { key: string; text: string }[] }>('adAccounts.status', {});
  if (!q.data?.notices.length) return null;
  return (
    <div className="bp-ads-notices">
      {q.data.notices.map((n) => (
        <p key={n.key} className="flw-sub flw-bad" role="alert">
          {n.text} Setup › Campaigns shows each account.
        </p>
      ))}
    </div>
  );
}

/** How the ads did, day by day or week by week, for a preset or picked days. */
function OverTime({ today, host }: { today: string; host: Host }) {
  const [preset, setPreset] = useState<TrendPreset>('30');
  const [picked, setPicked] = useState({ from: '', to: today });
  const range = trendRange(preset, today, picked);
  const q = useQuery<unknown>('reports.campaigns', 'error' in range ? null : range);
  return (
    <Section title="Over time">
      <div className="bp-ads-range">
        <div className="flw-seg" role="group" aria-label="Days">
          {TREND_PRESETS.map(([k, words]) => (
            <button key={k} type="button" aria-pressed={k === preset} onClick={() => setPreset(k)}>
              {words}
            </button>
          ))}
        </div>
        {preset === 'custom' && (
          <>
            <label>
              <span className="flw-sub">From</span>
              <input
                className="flw-field"
                type="date"
                value={picked.from}
                max={today}
                onChange={(e) => setPicked({ ...picked, from: e.target.value })}
              />
            </label>
            <label>
              <span className="flw-sub">To</span>
              <input
                className="flw-field"
                type="date"
                value={picked.to}
                onChange={(e) => setPicked({ ...picked, to: e.target.value })}
              />
            </label>
          </>
        )}
      </div>
      {'error' in range ? (
        <p className="flw-empty" role="status">
          {range.error}
        </p>
      ) : (
        <Drawn
          q={q}
          schema={AdsTrendProps}
          what="the days"
          build={(r: object) => ({
            ...r,
            unit: range.groupBy,
            label: `${range.groupBy === 'week' ? 'By week' : 'By day'}, ${range.from} to ${range.to}`,
          })}
          draw={(p) => <AdsTrend data={p} host={host} />}
        />
      )}
    </Section>
  );
}

/** A segmented control in the title block: one control, its choices pressed or not. */
function Seg<K extends string>({
  label,
  value,
  choices,
  onChange,
}: {
  label: string;
  value: K;
  choices: readonly (readonly [K, string])[];
  onChange: (k: K) => void;
}) {
  return (
    <div className="seg" role="group" aria-label={label}>
      {choices.map(([k, words]) => (
        <button
          key={k}
          className={`btn quiet${k === value ? ' on' : ''}`}
          aria-pressed={k === value}
          onClick={() => onChange(k)}
        >
          {words}
        </button>
      ))}
    </div>
  );
}

// ── How did the ads do? ──

export function AdsScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const today = todayIn(tz);
  const [period, setPeriod] = useState<Period>('today');
  const month = monthRange(today);
  const q = useQuery<unknown>('reports.campaigns', { ...periodRange(period, today), groupBy: 'channel' });
  const m = useQuery<unknown>('reports.campaigns', { ...periodRange('month', today), groupBy: 'channel' });
  const spend = useAllSpend(month);
  const host: Host = officeHost(onOpen);
  const words = PERIODS.find(([p]) => p === period)![1];
  return (
    <div className="app bp-home bp-ads">
      <Header
        nav={nav}
        title="How the ads did"
        lede="What the ads cost, the calls they brought, and what was booked, completed and invoiced from them."
      >
        <Seg label="Period" value={period} choices={PERIODS} onChange={setPeriod} />
      </Header>
      <main className="flw flw-office bp-home-body">
        <AccountNotices />
        <Section title="Totals">
          <Drawn
            q={q}
            schema={AdsSummaryProps}
            what="the ads"
            build={(r: object) => ({
              ...r,
              label: period === 'today' ? 'Today, so far' : `${words}, to today`,
              note:
                period === 'today' ? 'Completed, invoiced and revenue fill in as today’s jobs finish.' : null,
            })}
            draw={(p) => <AdsSummary data={p} host={host} />}
          />
        </Section>
        <div className="bp-grid2">
          <div>
            <Section title="Spend this month">
              <Drawn
                q={spend}
                schema={SpendProps}
                what="the month’s spend"
                build={(r: object) => ({ ...r, ...month, today })}
                draw={(p) => <SpendThisMonth data={p} host={host} />}
              />
            </Section>
          </div>
          <div>
            <Section title="From call to invoice">
              <Drawn
                q={m}
                schema={CampaignsReport}
                what="the month"
                draw={(p) => <AdsFunnel data={{ label: 'All ads this month', rows: p.rows }} host={host} />}
              />
            </Section>
          </div>
        </div>
        <OverTime today={today} host={host} />
        <AdChangesSection timeZone={tz} held={nav.permissions ?? []} host={host} />
      </main>
    </div>
  );
}

// ── Which campaigns work? ──

export function AdCampaignsScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const thisMonth = monthOf(todayIn(tz));
  const [[from, to], setMonth] = useState(thisMonth);
  const q = useQuery<unknown>('reports.campaigns', { from, to, groupBy: 'campaign' });
  const c = useQuery<{ campaigns: unknown[] }>('campaigns.list', { includeRetired: true });
  const reasons = useQuery<unknown>('reports.callReasons', { from, to });
  const host = officeHost(onOpen);
  const step = (by: 1 | -1) => setMonth(stepMonth(from, by));
  const month = monthName(from);
  return (
    <div className="app bp-home bp-ads">
      <Header
        nav={nav}
        title="Campaigns"
        lede="Which campaigns bring work back for what they cost, best first, and why people called."
      >
        <div className="seg" role="group" aria-label="Month">
          <button className="btn quiet" onClick={() => step(-1)}>
            ‹ {monthName(stepMonth(from, -1)[0]).split(' ')[0]}
          </button>
          <button className="btn quiet" disabled={from === thisMonth[0]} onClick={() => setMonth(thisMonth)}>
            This month
          </button>
          <button className="btn quiet" onClick={() => step(1)}>
            {monthName(stepMonth(from, 1)[0]).split(' ')[0]} ›
          </button>
        </div>
      </Header>
      <main className="flw flw-office bp-home-body">
        <Section title={`Campaigns, ${month}`}>
          <Drawn
            q={{ ...q, data: q.data && c.data ? q.data : null, error: q.error ?? c.error }}
            schema={CampaignTableProps}
            what="the campaigns"
            build={(r: object) => ({ ...r, label: 'Best return first', campaigns: c.data?.campaigns ?? [] })}
            draw={(p) => <CampaignTable data={p} host={host} />}
          />
        </Section>
        <Section title="Why they called">
          <Drawn
            q={reasons}
            schema={CallReasonsProps}
            what="the calls"
            build={(r: object) => ({ ...r, label: `Calls taken in ${month}` })}
            draw={(p) => <CallReasons data={p} host={host} />}
          />
        </Section>
      </main>
    </div>
  );
}

// ── Where did today's calls come from? ──

export function AdLeadsScreen({ nav, onOpen }: { nav: NavProps; onOpen: (t: OpenTarget) => void }) {
  const tz = tzOf(nav);
  const today = todayIn(tz);
  const [source, setSource] = useState('all');
  // today's inbound calls, in the company's day, from the source picked: filtered, counted and paged by
  // the server; each source's count is the server's too, of every call, not of the pages read (BL-110)
  const q = usePages<unknown>('calls.recent', leadsInput(today, source), 'calls');
  const counts = useQuery<{ sources?: SourceCount[] }>('calls.recent', sourceCountsInput(today));
  const conversions = maySeeConversions(nav.permissions ?? []);
  const conv = usePages<unknown>('adConversions.list', conversions ? {} : null, 'conversions');
  const host = officeHost(onOpen);
  const calls = q.data?.rows ?? [];
  const all = sources(counts.data?.sources ?? []);
  const choices = all.map((s) => [s.key, `${s.label} ${s.n}`] as const);
  return (
    <div className="app bp-home bp-ads">
      <Header
        nav={nav}
        title="Calls by source"
        lede="Where each of today’s calls came from, the number it rang, why they called, and what came of it."
      >
        {all[0]!.n > 0 && <Seg label="Source" value={source} choices={choices} onChange={setSource} />}
      </Header>
      <main className="flw flw-office bp-home-body">
        <Section title="Today" count={q.data?.total ?? 0}>
          <Drawn
            q={q}
            schema={CallsBySourceProps}
            what="today’s calls"
            build={() => ({ label: 'Newest first', timeZone: tz, calls })}
            draw={(p) => <CallsBySource data={p} host={host} />}
          />
          {q.more && (
            <p className="flw-more">
              <button type="button" className="flw-btn" onClick={q.more}>
                The next {Math.min(100, (q.data?.total ?? 0) - calls.length)} of {q.data?.total} calls
              </button>
            </p>
          )}
        </Section>
        {conversions && (
          <Section title="Sent to Google">
            <Drawn
              q={conv}
              schema={ConversionsProps}
              what="what was sent to Google"
              build={(r: { rows: unknown[]; total: number; next: string | null }) => ({
                conversions: r.rows,
                total: r.total,
                next: r.next,
                timeZone: tz,
              })}
              draw={(p) => <Conversions data={p} host={host} onMore={conv.more} />}
            />
          </Section>
        )}
      </main>
    </div>
  );
}
